import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './FJImageEnhancerImageItem.module.less';
import { useState, useEffect } from 'react';
import FJUtil from '../../../util/FJUtil';
import FJToolTip from '../../../common/components/FJToolTip/FJToolTip';
import ReloadIcon from '../../../assets/svg/reload.svg?react';
import DownloadIcon from '../../../assets/svg/download.svg?react';
import FJResourceItemLoading from '../../../common/components/FJResourceItemLoading/FJResourceItemLoading';
import FJLoading from '../../../common/components/FJLoading/FJLoading';
import { useRef } from 'react';
import FJSlideCompare from '../../../common/components/FJSlideCompare/FJSlideCompare';
import { useCallback } from 'react';

const FJImageEnhancerImageItem = props => {
    const { imgUrl, originalUrl, loading, onDownload } = props;
    const [stateImgUrl, setStateImgUrl] = useState(imgUrl);
    const [stateOriginImgUrl, setStateOriginImgUrl] = useState(originalUrl);

    const [stateIsLoading, setStateIsLoading] = useState(true);
    const [stateIsLoadingError, setStateIsLoadingError] = useState(false);

    const [stateOriginLoadingSuccess, setStateOriginLoadingSuccess] = useState(false);
    const [stateOriginLoadingError, setStateOriginLoadingError] = useState(false);

    const [stateIsDownloadLoading, setStateIsDownloadLoading] = useState(false);

    const [stateOriginImageSize, setStateOriginImageSize] = useState({ width: 0, height: 0 });
    const [stateImageSize, setStateImageSize] = useState({ width: 0, height: 0 });
    const [stateCompareLineScale, setStateCompareLineScale] = useState(0.3);
    const [editImageRect, setEditImageRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [originImageStyle, setOriginImageStyle] = useState({});
    const [afterImageStyle, setAfterImageStyle] = useState({});

    const imageRef = useRef(null);
    const originImageRef = useRef(null);
    const imgBoxRef = useRef(null);

    useEffect(() => {
        //加缓存
        setStateIsLoading(true);
        setStateOriginLoadingSuccess(false);
        FJUtil.getLocalStorageImage(imgUrl, url => {
            setStateImgUrl(url);
            setStateCompareLineScale(0.3);
        });
    }, [imgUrl]);

    useEffect(() => {
        setStateOriginImgUrl(originalUrl);
    }, [originalUrl]);

    const _onReload = e => {
        e.stopPropagation();

        if (stateIsLoadingError) {
            setStateImgUrl(FJUtil.getReloadNewUrl(imgUrl));
            setStateIsLoading(true);
        }
        if (stateOriginLoadingSuccess) {
            setStateOriginImgUrl(FJUtil.getReloadNewUrl(originalUrl));
        }
    };
    const _renderReload = () => {
        return (
            <div className={styles.retry_box}>
                <ReloadIcon className={styles.reload_icon} onClick={_onReload} />
            </div>
        );
    };
    const _onDownload = () => {
        if (stateIsDownloadLoading) {
            return;
        }
        setStateIsDownloadLoading(true);
        onDownload && onDownload(imgUrl, () => setStateIsDownloadLoading(false));
    };

    useEffect(() => {
        if (stateOriginLoadingSuccess && originImageRef.current) {
            const { naturalWidth: originWidth, naturalHeight: originHeight } = originImageRef.current;
            setStateOriginImageSize({
                width: originWidth,
                height: originHeight,
            });
        }
        if (!stateIsLoading && imageRef.current) {
            const { naturalWidth, naturalHeight } = imageRef.current;
            setStateImageSize({
                width: naturalWidth,
                height: naturalHeight,
            });
        }
    }, [stateOriginLoadingSuccess, stateIsLoading]);

    const _getEditImageRect = useCallback(() => {
        if (!stateOriginLoadingSuccess || !stateImageSize.width || !stateImageSize.height) {
            return { left: 0, top: 0, width: 0, height: 0 };
        }
        const { width: boxWidth, height: boxHeight } = imgBoxRef.current?.getBoundingClientRect() || {};

        const ratio = stateImageSize.width / stateImageSize.height;

        const width = Math.min(boxWidth, boxHeight * ratio);
        const height = Math.min(boxHeight, boxWidth / ratio);

        const left = Math.max(0, (boxWidth - width) / 2);
        const top = Math.max(0, (boxHeight - height) / 2);

        return {
            x: left,
            y: top,
            width: width,
            height: height,
        };
    }, [stateOriginLoadingSuccess, stateImageSize]);

    const _updateOriginImageStyle = useCallback(
        editImageRect => {
            if (!editImageRect.width || !editImageRect.height) {
                setOriginImageStyle({});
                return;
            }

            setOriginImageStyle({
                clipPath: `inset(0px ${editImageRect.x + editImageRect.width * (1 - stateCompareLineScale)}px 0px 0px)`,
            });
        },
        [stateCompareLineScale],
    );

    const _updateAfterImageStyle = useCallback(
        editImageRect => {
            if (!editImageRect.width || !editImageRect.height) {
                setAfterImageStyle({});
                return;
            }

            setAfterImageStyle({
                clipPath: `inset(0px 0px 0px ${editImageRect.x + editImageRect.width * stateCompareLineScale}px)`,
            });
        },
        [stateCompareLineScale],
    );

    const updateSlideCompareData = useCallback(() => {
        const newEditImageRect = _getEditImageRect();
        _updateOriginImageStyle(newEditImageRect);
        _updateAfterImageStyle(newEditImageRect);
        setEditImageRect(newEditImageRect);
    }, [_getEditImageRect, _updateOriginImageStyle, _updateAfterImageStyle]);

    useEffect(() => {
        updateSlideCompareData();

        window.addEventListener('resize', updateSlideCompareData);
        return () => {
            window.removeEventListener('resize', updateSlideCompareData);
        };
    }, [updateSlideCompareData]);

    return (
        <div ref={imgBoxRef} className={classNames(styles.box)}>
            <div className={styles.comparison_img_box} style={originImageStyle}>
                <img
                    className={classNames(styles.comparison_img, styles.show)}
                    src={stateOriginImgUrl}
                    ref={originImageRef}
                    alt=''
                    onLoad={() => {
                        setStateOriginLoadingSuccess(true);
                        setStateOriginLoadingError(false);
                    }}
                    onError={() => {
                        setStateOriginLoadingSuccess(false);
                        setStateOriginLoadingError(true);
                    }}
                    loading='lazy'
                />
            </div>
            <div className={styles.main_img_box} style={afterImageStyle}>
                <img
                    className={classNames(styles.main_img, styles.show)}
                    src={stateImgUrl}
                    ref={imageRef}
                    alt=''
                    onLoad={() => {
                        setStateIsLoadingError(false);
                        setStateIsLoading(false);
                    }}
                    onError={() => {
                        setStateIsLoadingError(true);
                        setStateIsLoading(false);
                    }}
                    loading='lazy'
                />
            </div>

            <FJSlideCompare
                className={styles.compare_box}
                editImageRect={editImageRect}
                compareLineScale={stateCompareLineScale}
                originImageSize={stateOriginImageSize}
                editImageSize={stateImageSize}
                active={
                    !(stateIsLoading || loading || stateOriginLoadingError) &&
                    editImageRect.width > 0 &&
                    editImageRect.height > 0
                }
                zoom={1}
                changeCompareLineScale={setStateCompareLineScale}
            />

            <div className={styles.header}>
                <FJToolTip title={'download'}>
                    <div className={styles.hover_box} onClick={_onDownload}>
                        {stateIsDownloadLoading ? <FJLoading style={{ width: 16, height: 16 }} /> : <DownloadIcon />}
                    </div>
                </FJToolTip>
            </div>

            {stateIsLoadingError && _renderReload()}
            {(stateIsLoading || loading || !stateOriginLoadingSuccess) && (
                <FJResourceItemLoading className={styles.loading} />
            )}
        </div>
    );
};

FJImageEnhancerImageItem.propTypes = {
    imgUrl: PropTypes.string,
    originalUrl: PropTypes.string,
    loading: PropTypes.bool,
    onDownload: PropTypes.func,
};

export default FJImageEnhancerImageItem;
